import api from './apiClient'; // 共有apiClientをインポート
import AuthService from './authService';
import { Customer, CustomerUpdateInput } from '../types/customer'; // CustomerUpdateInput をインポート

// エラーレスポンスのインターフェース定義
interface ErrorResponse {
  message?: string;
  error?: string;
}

// フィルター用インターフェース
export interface UserFilter {
  search?: string;
  role?: string;
  status?: string;
  shift_roles_only?: boolean;
}

// レスポンス用インターフェース
export interface UserListResponse {
  users: User[];
  total: number;
  page?: number;
  pageSize?: number;
}

// ユーザーインターフェース
export interface User {
  id: number;
  username: string;
  email: string | null;  // 顧客はemailなしの場合もある
  password?: string; // パスワードはオプショナルで追加
  role: string;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  phone?: string | null;  // バックエンド互換用
  line_id?: string | null;  // LINE ID
  line_account?: string | null; // line_accountを追加
  is_active: boolean;
  status: 'active' | 'inactive';
  profile_image?: string; // profile_imageを追加

  // 関連データをオプショナルプロパティとして追加
  customer?: Customer; // anyからCustomer型に変更
  cast?: any;     // Cast情報を保持
  
  // キャスト専用フィールド
  entry_date?: string | null;
  photo_url?: string | null;
  age?: number | null;
  height?: number | null;
  bust?: number | null;
  cup_size?: string | null;
  waist?: number | null;
  hip?: number | null;
  nomination_fee?: number | null;  // 指名料
  special_nomination_fee?: number | null;  // 本指名料
  notes?: string | null;
  
  // 関連データ
  options?: CastOption[];
  blocked_customers?: BlockedCustomer[];
  phone_numbers?: PhoneNumber[];
}

// ユーザー更新用のペイロード型を定義
export interface UserUpdatePayload extends Omit<Partial<User>, 'customer' | 'cast'> {
  customer?: CustomerUpdateInput; // Partial<Customer> から CustomerUpdateInput に変更
  cast?: any; // castも部分的な型が望ましいが、一旦anyで対処
}

// キャストオプションインターフェース
export interface CastOption {
  id: number;
  cast_id: number;
  option_name: string;
  price: number;
}

// NG客インターフェース
export interface BlockedCustomer {
  id: number;
  cast_id: number;
  phone_number: string;
  reason?: string;
  blocked_at: string;
}

// 追加電話番号インターフェース
export interface PhoneNumber {
  id: number;
  user_id: number;
  phone_number: string;
  is_primary: boolean;
}


class UserService {
  // ユーザー一覧の取得
  async getUsers(
    page: number = 1, 
    per_page: number = 10, 
    filter: UserFilter = {}
  ): Promise<UserListResponse> {
    try {
      const params: any = { page, per_page, ...filter };
      // エンドポイントを新しいものに修正
      const response = await api.get('/users_edit/list', { params });
      return response.data;
    } catch (error) {
      console.error('ユーザー一覧の取得に失敗しました:', error);
      throw error;
    }
  }

  // getUser以降は新しい /users_edit を使用
  async getUser(userId: number): Promise<User> {
    try {
      const response = await api.get(`/users_edit/${userId}`);
      return response.data;
    } catch (error) {
      console.error(`ユーザー(ID: ${userId})の取得に失敗しました:`, error);
      throw error;
    }
  }

  async createUser(userData: Partial<User>): Promise<User> {
    try {
      const response = await api.post('/users_edit/create', userData); // エンドポイント変更
      return response.data;
    } catch (error) {
      console.error('ユーザーの作成に失敗しました:', error);
      throw error;
    }
  }

  async updateUser(userId: number, userData: UserUpdatePayload): Promise<User> {
    try {
      const response = await api.put(`/users_edit/${userId}`, userData); // エンドポイント変更
      return response.data;
    } catch (error) {
      console.error(`ユーザー(ID: ${userId})の更新に失敗しました:`, error);
      throw error;
    }
  }

  async deleteUser(userId: number): Promise<void> {
    try {
      await api.delete(`/users_edit/${userId}`); // エンドポイント変更
    } catch (error) {
      console.error(`ユーザー(ID: ${userId})の削除に失敗しました:`, error);
      throw error;
    }
  }

  // ユーザーステータスの切り替え
  async toggleUserStatus(userId: number): Promise<User> {
    try {
      const response = await api.post(`/users_edit/${userId}/toggle-status`, {});
      return response.data.user;
    } catch (error: any) {
      console.error(`ユーザー(ID: ${userId})のステータス切り替えに失敗しました:`, error);
      const errorMessage = (error.response?.data?.message) || error.message;
      throw new Error(`ユーザーステータスの更新に失敗しました: ${errorMessage}`);
    }
  }

  async getRoles(): Promise<string[]> {
    try {
      const response = await api.get('/users_edit/roles');
      return response.data;
    } catch (error) {
      console.error('役割一覧の取得に失敗しました:', error);
      throw error;
    }
  }

  async importCsv(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post('/users_edit/import_csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      return response.data;
    } catch (error) {
      console.error('CSVインポートエラー:', error);
      throw error;
    }
  }
  
  async exportCsv(filter: UserFilter = {}): Promise<Blob> {
    try {
      const response = await api.get('/users_edit/export_csv', {
        params: filter,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('CSVエクスポートエラー:', error);
      throw error;
    }
  }
}

const userService = new UserService();
export default userService;